import { useMutation } from '@apollo/react-hooks';
import { Typography } from '@bowtie-ins/bowkit';
import {
  Forward,
  BookmarkEmpty,
  BookmarkFilled,
} from '@bowtie-ins/bowkit/icons';
import { GRAPHITE, GREEN_DAY, PEBBLE, WHITE } from '@bowtie-ins/bowkit/palette';
import {
  DefaultCouponMapping,
  Promotion,
  PROMOTION_VALIDATION,
  RewardSet,
  useAuthenticated,
  useVariant,
  notifier,
  UnexpectedError,
} from '@bowtie-web/common';
import useSaveCoupon from '../hooks/useSaveCoupon';
import useListSavedCoupons from '../hooks/useListSavedCoupons';
import { LIST_SAVED_COUPONS } from '@bowtie-web/common/lib/graphql';
import {
  Markdown,
  MarkdownOverrides,
  shadows,
  TextField,
} from '@bowtie-web/components';
import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Collapse,
} from '@material-ui/core';
import InputAdornment from '@material-ui/core/InputAdornment';
import Link from '@material-ui/core/Link';
import dayjs from 'dayjs';
import { useFormik } from 'formik';
import { useCallback, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import getLogger from '../logging';
import { useEffectOnce, useMap, useToggle } from 'react-use';
import { InferType, object, string } from 'yup';
import { QuotationContextValue } from '../contexts/QuotationContext';
import AnimatedDot from './components/AnimatedDot';
import CouponsMenu from './CouponsMenu/CouponsMenu';
import NoUserSaveCouponModal from './SaveCoupon/NoUserSaveCouponModal';
import SaveCouponTooltip from './SaveCoupon/SaveCouponTooltip';

const logger = getLogger('coupon');

export type CouponOption = {
  couponCode: string;
  defaultCouponMapping: DefaultCouponMapping;
  rewardSet: RewardSet;
};

type Props = {
  couponCode: string;
  onCouponChange: QuotationContextValue['setCoupon'];
  publicCouponOptions: CouponOption[];
  existingCustomerCouponOptions: CouponOption[];
  productCodes: string[];
} & Pick<QuotationContextValue, 'coupon' | 'isIssueAgeValid' | 'activeProduct'>;

const schema = () =>
  object({
    coupon_code: string().trim().uppercase().required(),
  });

type Schema = InferType<ReturnType<typeof schema>>;

export const CUSTOM_COMPONENTS: MarkdownOverrides = {
  p: ({ children }) => (
    <span style={{ margin: 0, whiteSpace: 'pre-line', color: 'inherit' }}>
      {children}
    </span>
  ),
  a: props => (
    <Link
      {...props}
      target="_blank"
      rel="noreferrer noopener"
      component="a"
      style={{
        color: 'inherit',
        fontSize: 'inherit',
        lineHeight: 'inherit',
        fontWeight: 'inherit',
        textDecorationLine: 'underline',
      }}
    />
  ),
  remarks: ({ children }) => (
    <span style={{ margin: 0, whiteSpace: 'pre-line', color: GRAPHITE }}>
      {children}
    </span>
  ),
};

const CouponForm = ({
  couponCode,
  coupon,
  onCouponChange,
  isIssueAgeValid,
  activeProduct,
  publicCouponOptions = [],
  existingCustomerCouponOptions = [],
  productCodes,
}: Props) => {
  const intl = useIntl();
  const [cachedCouponsMapping, { set: addCouponToMapping }] = useMap<
    Record<string, Promotion>
  >({});
  const [couponsMenuOpen, toggleCouponsMenuOpen] = useToggle(false);
  const [noUserSaveModalOpen, setNoUserSaveModalOpen] = useState(false);

  const textFieldRef = useRef<HTMLInputElement>(null);

  const isAuthenticated = useAuthenticated();

  const [saveCoupon, { loading: isSavingCoupon }] = useSaveCoupon();
  const [savedCoupons] = useListSavedCoupons(
    activeProduct?.code
      ? { applicable_product_code: activeProduct.code }
      : { applicable_product_code: '' },
    { skip: !isAuthenticated || !activeProduct?.code },
  );

  const { value: isCouponSavingEnabled } = useVariant('enable_coupon_saving');
  const [hasNewlySavedCoupon, setHasNewlySavedCoupon] = useState(false);

  const savedCouponCodes = useMemo(() => {
    return savedCoupons?.map(coupon => coupon.code) || [];
  }, [savedCoupons]);
  const [validate] = useMutation<{ promotionValidate: Promotion }>(
    PROMOTION_VALIDATION,
  );
  const [isInvalidCode, setIsInvalidCode] = useState(false);

  const onSubmit = useCallback(
    async (values: Schema) => {
      try {
        const { data } = await validate({
          variables: {
            product_id: activeProduct?.code,
            promo_code: values.coupon_code,
          },
        });
        if (data?.promotionValidate) {
          onCouponChange(data.promotionValidate);
          addCouponToMapping(values.coupon_code, data.promotionValidate);
        }
      } catch {
        onCouponChange(undefined);
        setIsInvalidCode(true);
      }
    },
    [activeProduct?.code, addCouponToMapping, onCouponChange, validate],
  );

  const cachedCouponOptions = useMemo(
    () => Object.values(cachedCouponsMapping),
    [cachedCouponsMapping],
  );

  const {
    values,
    isSubmitting,
    isValid,
    submitForm,
    handleSubmit,
    setFieldValue,
  } = useFormik<Schema>({
    initialValues: { coupon_code: couponCode },
    validateOnMount: true,
    validateOnChange: true,
    validateOnBlur: true,
    validationSchema: schema,
    onSubmit,
  });

  const onChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onCouponChange(undefined);
      setIsInvalidCode(false);
      setFieldValue(
        'coupon_code',
        event.target.value?.replace(/\s/g, '').toUpperCase() || '',
      );
    },
    [onCouponChange, setFieldValue],
  );

  const disabledField = useMemo(() => {
    return !isIssueAgeValid || isSubmitting || !activeProduct;
  }, [isIssueAgeValid, isSubmitting, activeProduct]);

  const disabledSubmit = useMemo(() => {
    return disabledField || !isValid || isSubmitting;
  }, [disabledField, isSubmitting, isValid]);

  const showCouponMenu = useMemo(() => {
    return (
      !!publicCouponOptions?.length || !!existingCustomerCouponOptions?.length
    );
  }, [publicCouponOptions, existingCustomerCouponOptions]);

  const isCouponSaved = useMemo(() => {
    return coupon?.promo_code
      ? savedCouponCodes.includes(coupon.promo_code)
      : false;
  }, [coupon?.promo_code, savedCouponCodes]);

  const helperText = useMemo(() => {
    if (coupon) {
      const couponSaved = savedCoupons.find(c => c.code === coupon.promo_code);
      return (
        <Typography as="span" variant="body-s" style={{ color: GREEN_DAY }}>
          <FormattedMessage
            id="coupon.message"
            values={{
              date: dayjs(
                couponSaved?.effective_to || coupon.display_effective_to,
              )
                .locale(intl.locale)
                .format('LL'),
              message: (
                <Markdown options={{ overrides: CUSTOM_COMPONENTS }}>
                  {coupon.message}
                </Markdown>
              ),
            }}
          />
        </Typography>
      );
    } else if (isInvalidCode) {
      return intl.formatMessage({ id: 'coupon.not_found' });
    } else {
      return '';
    }
  }, [coupon, intl, isInvalidCode, savedCoupons]);

  const handleCouponSelect = useCallback(
    async (couponCode?: string) => {
      if (couponCode === coupon?.promo_code) {
        return;
      }

      onCouponChange(undefined);
      setIsInvalidCode(false);
      await setFieldValue('coupon_code', couponCode);

      if (!couponCode) {
        textFieldRef?.current?.focus();
      } else {
        submitForm();
      }
    },
    [coupon?.promo_code, onCouponChange, setFieldValue, submitForm],
  );

  const handleSaveClick = useCallback(async () => {
    if (!isAuthenticated) {
      setNoUserSaveModalOpen(true);
    } else {
      // User is authenticated, toggle save/unsave
      if (coupon?.promo_code && coupon?.allow_save) {
        try {
          if (!isCouponSaved) {
            await saveCoupon(coupon.promo_code, {
              refetchQueries: productCodes.map(productCode => ({
                query: LIST_SAVED_COUPONS,
                variables: {
                  applicable_product_code: productCode,
                },
              })),
            });
            setHasNewlySavedCoupon(true);
          }
        } catch (error) {
          logger.raise(
            new UnexpectedError(
              `Failed to save coupon ${coupon.promo_code}`,
              error,
            ),
          );
          notifier.error({
            body: intl.formatMessage({ id: 'coupon.save.error' }),
            timeout: 5000,
          });
        }
      }
    }
  }, [
    isAuthenticated,
    coupon,
    isCouponSaved,
    saveCoupon,
    productCodes,
    setHasNewlySavedCoupon,
    intl,
  ]);

  useEffectOnce(() => {
    // `submitForm` will validate form before submitting, API call is
    // avoided if no default coupon code is provided.
    submitForm();
  });

  return (
    <form onSubmit={handleSubmit}>
      <Box
        display="flex"
        flexDirection="column"
        bgcolor={PEBBLE}
        boxShadow={shadows[2]}
        borderRadius={8}
      >
        <Box bgcolor={WHITE} borderRadius={8}>
          <TextField
            name="coupon_code"
            label={<FormattedMessage id="coupon.coupon_code" />}
            value={values.coupon_code}
            disabled={disabledField}
            error={isInvalidCode}
            helperText={helperText}
            onChange={onChange}
            inputRef={textFieldRef}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {coupon ? (
                    isCouponSavingEnabled &&
                    showCouponMenu &&
                    coupon.allow_save === true ? (
                      <SaveCouponTooltip isVisible={!isCouponSaved}>
                        <Button
                          aria-label="save coupon code"
                          type="button"
                          disabled={disabledField}
                          variant="text"
                          color="secondary"
                          style={{
                            fontWeight: 'bold',
                            textTransform: 'lowercase',
                            pointerEvents:
                              isSavingCoupon || isCouponSaved ? 'none' : 'auto',
                          }}
                          data-ui-component="Input"
                          data-ui-variant="Code"
                          data-ui-action="Save"
                          onClick={handleSaveClick}
                          endIcon={
                            isCouponSaved ? (
                              <BookmarkFilled
                                style={{ color: 'inherit', fontSize: 24 }}
                              />
                            ) : (
                              <BookmarkEmpty
                                style={{ color: 'inherit', fontSize: 24 }}
                              />
                            )
                          }
                        >
                          {isSavingCoupon ? (
                            <CircularProgress size={20} />
                          ) : (
                            <FormattedMessage
                              id={
                                isCouponSaved ? 'coupon.saved' : 'coupon.save'
                              }
                            />
                          )}
                        </Button>
                      </SaveCouponTooltip>
                    ) : (
                      <></>
                    )
                  ) : (
                    <Button
                      aria-label="redeem coupon code"
                      type="submit"
                      disabled={disabledSubmit}
                      color="primary"
                      style={{ fontWeight: 'bold', textTransform: 'lowercase' }}
                      data-ui-component="Input"
                      data-ui-variant="Code"
                      data-ui-action="Submit"
                    >
                      <FormattedMessage id="coupon.redeem" />
                    </Button>
                  )}
                </InputAdornment>
              ),
            }}
            containerProps={{ style: { borderRadius: 8 } }}
            data-ui-component="Input"
            data-ui-variant="Code"
          />
        </Box>
        <Collapse in={showCouponMenu} timeout={600}>
          <ButtonBase
            disableRipple
            style={{ width: '100%' }}
            onClick={() => toggleCouponsMenuOpen(true)}
            data-ui-component="Button"
            data-ui-variant="Quotation Coupons Menu"
            data-ui-action="Open"
          >
            <Box
              color={GRAPHITE}
              display="grid"
              gridTemplateColumns="auto auto 14px"
              alignItems="center"
              padding="8px 16px"
              width="100%"
              textAlign="left"
            >
              <Box display="flex" alignItems="center" gridGap={4}>
                <Typography>
                  <FormattedMessage id="coupon.coupon_options" />
                </Typography>
                <AnimatedDot visible={hasNewlySavedCoupon} />
              </Box>
              <div />
              <Forward style={{ width: '14px', height: '14px' }} />
            </Box>
          </ButtonBase>
        </Collapse>
      </Box>

      <CouponsMenu
        open={couponsMenuOpen}
        publicCouponOptions={publicCouponOptions}
        existingCustomerCouponOptions={existingCustomerCouponOptions}
        cachedCouponOptions={cachedCouponOptions}
        savedCouponCodes={savedCouponCodes}
        savedCoupons={savedCoupons}
        hasNewlySavedCoupon={hasNewlySavedCoupon}
        promotionSelected={coupon}
        onCouponSelected={handleCouponSelect}
        onClose={() => {
          toggleCouponsMenuOpen(false);
          setHasNewlySavedCoupon(false);
        }}
      />

      <NoUserSaveCouponModal
        open={noUserSaveModalOpen}
        onClose={() => setNoUserSaveModalOpen(false)}
      />
    </form>
  );
};

export default CouponForm;
